import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import useAuthStore from './store/authStore';
import { canUserAccess } from './utils/permissions';
import { getPermissionForPath } from './utils/routePermissions';
import { coreAPI } from './services/api';

// Components
import Layout from './components/Layout';
import AppShellBanner from './components/AppShellBanner';
import PersianDateGuard from './components/PersianDateGuard';

// Pages (route-level code splitting)
// هر صفحه فقط هنگام ورود به همان مسیر بارگیری می‌شود تا شروع سامانه سبک بماند.
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const DailyPlanPage = lazy(() => import('./pages/DailyPlanPage'));
const MorningDashboardPage = lazy(() => import('./pages/MorningDashboardPage'));
const CowsPage = lazy(() => import('./pages/CowsPage'));
const CowMovementHistoryReportPage = lazy(() => import('./pages/CowMovementHistoryReportPage'));
const DailyHerdSnapshotPage = lazy(() => import('./pages/DailyHerdSnapshotPage'));
const DuplicateCowMergePage = lazy(() => import('./pages/DuplicateCowMergePage'));
const BaharbandsPage = lazy(() => import('./pages/BaharbandsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const OperationalPeoplePage = lazy(() => import('./pages/OperationalPeoplePage'));
const RegisteredDataEditPage = lazy(() => import('./pages/RegisteredDataEditPage'));
const BackupManagerPage = lazy(() => import('./pages/BackupManagerPage'));
const ProtocolsPage = lazy(() => import('./pages/ProtocolsPage'));
const TreatmentsPage = lazy(() => import('./pages/TreatmentsPage'));
const HealthDashboardPage = lazy(() => import('./pages/HealthDashboardPage'));
const HealthTreatmentWorkbenchPage = lazy(() => import('./pages/HealthTreatmentWorkbenchPage'));
const HospitalAdmissionsPage = lazy(() => import('./pages/HospitalAdmissionsPage'));
const FreshCowMonitoringPage = lazy(() => import('./pages/FreshCowMonitoringPage'));
const LabTestsPage = lazy(() => import('./pages/LabTestsPage'));
const NewTreatmentPage = lazy(() => import('./pages/NewTreatmentPage'));
const TreatmentBatchDetailPage = lazy(() => import('./pages/TreatmentBatchDetailPage'));
const ProtocolFormPage = lazy(() => import('./pages/ProtocolFormPage'));
const DrugsPage = lazy(() => import('./pages/DrugsPage'));
const DrugLotsPage = lazy(() => import('./pages/DrugLotsPage'));
const MedicationWithdrawalReportPage = lazy(() => import('./pages/MedicationWithdrawalReportPage'));
const TreatmentOutcomeAnalyticsPage = lazy(() => import('./pages/TreatmentOutcomeAnalyticsPage'));
const EstrusNewPage = lazy(() => import('./pages/EstrusNewPage'));
const EstrusEventsPage = lazy(() => import('./pages/EstrusEventsPage'));
const VetVisitPage = lazy(() => import('./pages/VetVisitPage'));
const ReproServicesPage = lazy(() => import('./pages/ReproServicesPage'));
const ReproPregnancyChecksPage = lazy(() => import('./pages/ReproPregnancyChecksPage'));
const ReproRevisitsPage = lazy(() => import('./pages/ReproRevisitsPage'));
const ReproAbortionsPage = lazy(() => import('./pages/ReproAbortionsPage'));
const ReproDryOffsPage = lazy(() => import('./pages/ReproDryOffsPage'));
const ReproCalvingsPage = lazy(() => import('./pages/ReproCalvingsPage'));
const ReproTimelinePage = lazy(() => import('./pages/ReproTimelinePage'));
const ReproDashboardPage = lazy(() => import('./pages/ReproDashboardPage'));
const ReproPlannerPage = lazy(() => import('./pages/ReproPlannerPage'));
const ReproServiceDaysRangePage = lazy(() => import('./pages/ReproServiceDaysRangePage'));
const ReproCalvingForecastPage = lazy(() => import('./pages/ReproCalvingForecastPage'));
const ReproFertilityNeglectedMilkingPage = lazy(() => import('./pages/ReproFertilityNeglectedMilkingPage'));
const ReproCalvingInformationReportPage = lazy(() => import('./pages/ReproCalvingInformationReportPage'));
const ReproComprehensiveReportsPage = lazy(() => import('./pages/ReproComprehensiveReportsPage'));
const ReproLegacyFertilityReportsPage = lazy(() => import('./pages/ReproLegacyFertilityReportsPage'));
const ReproVisitPlansPage = lazy(() => import('./pages/ReproVisitPlansPage'));
const HerdExecutiveDashboardPage = lazy(() => import('./pages/HerdExecutiveDashboardPage'));
const Phase1OperationalReportsPage = lazy(() => import('./pages/Phase1OperationalReportsPage'));
const Phase2OperationalFertilityReportsPage = lazy(() => import('./pages/Phase2OperationalFertilityReportsPage'));
const Phase1AnalyticalReportsPage = lazy(() => import('./pages/Phase1AnalyticalReportsPage'));
const Phase4FertilityAnalyticsPage = lazy(() => import('./pages/Phase4FertilityAnalyticsPage'));
const Phase5ProductionHealthCalfAnalyticsPage = lazy(() => import('./pages/Phase5ProductionHealthCalfAnalyticsPage'));
const Phase6BaharbandPeopleQualityAnalyticsPage = lazy(() => import('./pages/Phase6BaharbandPeopleQualityAnalyticsPage'));
const Phase7ManagementExecutiveReportPage = lazy(() => import('./pages/Phase7ManagementExecutiveReportPage'));
const Phase9SmartAlertCenterPage = lazy(() => import('./pages/Phase9SmartAlertCenterPage'));
const Phase10ManagementSettingsPage = lazy(() => import('./pages/Phase10ManagementSettingsPage'));
const Phase11CustomReportBuilderPage = lazy(() => import('./pages/Phase11CustomReportBuilderPage'));
const AnalystLabPage = lazy(() => import('./pages/AnalystLabPage'));
const PregnancyCycleAnalyticsPage = lazy(() => import('./pages/PregnancyCycleAnalyticsPage'));
const ManagementCockpitPage = lazy(() => import('./pages/ManagementCockpitPage'));
const Phase12ReportPerformanceCenterPage = lazy(() => import('./pages/Phase12ReportPerformanceCenterPage'));
const Phase13HerdSnapshotHistoryPage = lazy(() => import('./pages/Phase13HerdSnapshotHistoryPage'));
const Phase14EconomicDecisionAnalyticsPage = lazy(() => import('./pages/Phase14EconomicDecisionAnalyticsPage'));
const Phase15PredictiveRiskAnalyticsPage = lazy(() => import('./pages/Phase15PredictiveRiskAnalyticsPage'));
const Phase22CalvingTransitionAnalyticsPage = lazy(() => import('./pages/Phase22CalvingTransitionAnalyticsPage'));
const Phase24LactationHeiferAnalyticsPage = lazy(() => import('./pages/Phase24LactationHeiferAnalyticsPage'));
const HeiferServiceAgeGestationReportPage = lazy(() => import('./pages/HeiferServiceAgeGestationReportPage'));
const SemenInventoryPage = lazy(() => import('./pages/SemenInventoryPage'));
const CowIndividualSelectPage = lazy(() => import('./pages/CowIndividualSelectPage'));
const CowIndividualPage = lazy(() => import('./pages/CowIndividualPage'));
const CowCardPage = lazy(() => import('./pages/CowCardPage'));
const SavedListsPage = lazy(() => import('./pages/SavedListsPage'));
const MilkRecordsPage = lazy(() => import('./pages/MilkRecordsPage'));
const MilkQualityReportPage = lazy(() => import('./pages/MilkQualityReportPage'));
const AdvancedMilkAnalyticsPage = lazy(() => import('./pages/AdvancedMilkAnalyticsPage'));
const MilkHealthImpactPage = lazy(() => import('./pages/MilkHealthImpactPage'));
const FertilityHealthImpactPage = lazy(() => import('./pages/FertilityHealthImpactPage'));
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage'));
const UserDailyActivityPage = lazy(() => import('./pages/UserDailyActivityPage'));
const ImportJobsPage = lazy(() => import('./pages/ImportJobsPage'));
const MedicationBatchesPage = lazy(() => import('./pages/MedicationBatchesPage'));
const DiseaseMasterDataPage = lazy(() => import('./pages/DiseaseMasterDataPage'));
const VetHealthReportPage = lazy(() => import('./pages/VetHealthReportPage'));
const UdderLamenessReportPage = lazy(() => import('./pages/UdderLamenessReportPage'));
const UdderLamenessOperationsPage = lazy(() => import('./pages/UdderLamenessOperationsPage'));
const CalvesPage = lazy(() => import('./pages/CalvesPage'));
const CalfPhotoSketchPage = lazy(() => import('./pages/CalfPhotoSketchPage'));
const CalfImageSearchHistoryPage = lazy(() => import('./pages/CalfImageSearchHistoryPage'));
const CalfEventsPage = lazy(() => import('./pages/CalfEventsPage'));
const CalfMilkRecordsPage = lazy(() => import('./pages/CalfMilkRecordsPage'));
const CowDocumentsPage = lazy(() => import('./pages/CowDocumentsPage'));
const PreventiveCarePage = lazy(() => import('./pages/PreventiveCarePage'));
const UdderHealthPage = lazy(() => import('./pages/UdderHealthPage'));
const LamenessEventsPage = lazy(() => import('./pages/LamenessEventsPage'));
const VetPhase2SummaryPage = lazy(() => import('./pages/VetPhase2SummaryPage'));
const FeedInventoryPage = lazy(() => import('./pages/FeedInventoryPage'));
const FeedCategoriesPage = lazy(() => import('./pages/FeedCategoriesPage'));
const FeedWasteCausesPage = lazy(() => import('./pages/FeedWasteCausesPage'));
const FeedLossCausesPage = lazy(() => import('./pages/FeedLossCausesPage'));
const FeedResidualCausesPage = lazy(() => import('./pages/FeedResidualCausesPage'));
const FeedOverheadTypesPage = lazy(() => import('./pages/FeedOverheadTypesPage'));
const FeedingGroupsPage = lazy(() => import('./pages/FeedingGroupsPage'));
const RationsPage = lazy(() => import('./pages/RationsPage'));
const RationDetailPage = lazy(() => import('./pages/RationDetailPage'));
const FeedPurchasesPage = lazy(() => import('./pages/FeedPurchasesPage'));
const FeedPlannedConsumptionsPage = lazy(() => import('./pages/FeedPlannedConsumptionsPage'));
const FeedScaleTicketsPage = lazy(() => import('./pages/FeedScaleTicketsPage'));
const FeedScaleDistributionsPage = lazy(() => import('./pages/FeedScaleDistributionsPage'));
const FeedConsumptionPage = lazy(() => import('./pages/FeedConsumptionPage'));
const FeedWastesPage = lazy(() => import('./pages/FeedWastesPage'));
const FeedLossesPage = lazy(() => import('./pages/FeedLossesPage'));
const FeedResidualsPage = lazy(() => import('./pages/FeedResidualsPage'));
const FeedOverheadsPage = lazy(() => import('./pages/FeedOverheadsPage'));
const FeedOperationalCostPage = lazy(() => import('./pages/FeedOperationalCostPage'));
const FeedDashboardPage = lazy(() => import('./pages/FeedDashboardPage'));
const FeedIOFCPage = lazy(() => import('./pages/FeedIOFCPage'));
const MilkBaharbandPerformancePage = lazy(() => import('./pages/MilkBaharbandPerformancePage'));
const CalfFeedAgeIntakePage = lazy(() => import('./pages/CalfFeedAgeIntakePage'));
const FeedAlertDashboardPage = lazy(() => import('./pages/FeedAlertDashboardPage'));
const FeedPerformanceAnalyticsPage = lazy(() => import('./pages/FeedPerformanceAnalyticsPage'));
const FeedManagementReportsPage = lazy(() => import('./pages/FeedManagementReportsPage'));
const FeedDailyFastEntryPage = lazy(() => import('./pages/FeedDailyFastEntryPage'));
const FeedBaharbandRationPage = lazy(() => import('./pages/FeedBaharbandRationPage'));
const FeedInventoryRationFormulaPage = lazy(() => import('./pages/FeedInventoryRationFormulaPage'));
const FeedStartGuidePage = lazy(() => import('./pages/FeedStartGuidePage'));
const FeedExcelImportPage = lazy(() => import('./pages/FeedExcelImportPage'));
const SuppliersPage = lazy(() => import('./pages/SuppliersPage'));
const CounterpartiesPage = lazy(() => import('./pages/CounterpartiesPage'));
const SupplyInventoryPage = lazy(() => import('./pages/SupplyInventoryPage'));
const SupplyPurchasesPage = lazy(() => import('./pages/SupplyPurchasesPage'));
const SupplyConsumptionPage = lazy(() => import('./pages/SupplyConsumptionPage'));
const PurchaseRequestsPage = lazy(() => import('./pages/PurchaseRequestsPage'));
const PurchaseOrdersPage = lazy(() => import('./pages/PurchaseOrdersPage'));
const InventoryAlertsPage = lazy(() => import('./pages/InventoryAlertsPage'));
const Phase3OperationalSummaryPage = lazy(() => import('./pages/Phase3OperationalSummaryPage'));
const StorageLocationsPage = lazy(() => import('./pages/StorageLocationsPage'));
const SemenStoragePositionsPage = lazy(() => import('./pages/SemenStoragePositionsPage'));
const GoodsReceiptsPage = lazy(() => import('./pages/GoodsReceiptsPage'));
const OpeningInventoriesPage = lazy(() => import('./pages/OpeningInventoriesPage'));
const StockCountsPage = lazy(() => import('./pages/StockCountsPage'));
const InventoryOnHandPage = lazy(() => import('./pages/InventoryOnHandPage'));
const Phase4ExecutiveDashboardPage = lazy(() => import('./pages/Phase4ExecutiveDashboardPage'));
const KpiCenterPage = lazy(() => import('./pages/KpiCenterPage'));
const DataQualityCenterPage = lazy(() => import('./pages/DataQualityCenterPage'));
const ReportCommandCenterPage = lazy(() => import('./pages/ReportCommandCenterPage'));
const CalfReportCenterPage = lazy(() => import('./pages/CalfReportCenterPage'));
const EconomicWorkbenchPage = lazy(() => import('./pages/EconomicWorkbenchPage'));
const SiteComparisonPage = lazy(() => import('./pages/SiteComparisonPage'));
const EnterpriseAnalyticsPage = lazy(() => import('./pages/EnterpriseAnalyticsPage'));
const PowerUserPage = lazy(() => import('./pages/PowerUserPage'));
const Phase4CostCentersPage = lazy(() => import('./pages/Phase4CostCentersPage'));
const Phase4OperatingExpensesPage = lazy(() => import('./pages/Phase4OperatingExpensesPage'));
const Phase4ManagementCostAnalysisPage = lazy(() => import('./pages/Phase4ManagementCostAnalysisPage'));
const Phase4AllocationRulesPage = lazy(() => import('./pages/Phase4AllocationRulesPage'));
const AlertsPage = lazy(() => import('./pages/AlertsPage'));
const VetSmartAlertsPage = lazy(() => import('./pages/VetSmartAlertsPage'));
const VetCommandCenterPage = lazy(() => import('./pages/VetCommandCenterPage'));
const LactationCurvePage = lazy(() => import('./pages/LactationCurvePage'));
const VaccinationProtocolsPage = lazy(() => import('./pages/VaccinationProtocolsPage'));
const CalfMilkProtocolsPage = lazy(() => import('./pages/CalfMilkProtocolsPage'));
const CalfMilkMonthlyReportPage = lazy(() => import('./pages/CalfMilkMonthlyReportPage'));
const CalfMortalityReportPage = lazy(() => import('./pages/CalfMortalityReportPage'));
const CalfAnalyticsDashboardPage = lazy(() => import('./pages/CalfAnalyticsDashboardPage'));
const CalfGrowthAnalyticsPage = lazy(() => import('./pages/CalfGrowthAnalyticsPage'));
const CalfClinicalScoringPage = lazy(() => import('./pages/CalfClinicalScoringPage'));
const LivestockExitCausesPage = lazy(() => import('./pages/LivestockExitCausesPage'));
const CalfDailyTasksPage = lazy(() => import('./pages/CalfDailyTasksPage'));
const CalfGrowthWeighingPage = lazy(() => import('./pages/CalfGrowthWeighingPage'));
const CalfTreatmentSheetsPage = lazy(() => import('./pages/CalfTreatmentSheetsPage'));
const CowTreatmentSheetsPage = lazy(() => import('./pages/CowTreatmentSheetsPage'));
const CalfTreatmentProtocolsPage = lazy(() => import('./pages/CalfTreatmentProtocolsPage'));
const CowTreatmentProtocolsPage = lazy(() => import('./pages/CowTreatmentProtocolsPage'));
const CalfTreatmentOpsBoardPage = lazy(() => import('./pages/CalfTreatmentOpsBoardPage'));
const CalfTreatmentFinancialPage = lazy(() => import('./pages/CalfTreatmentFinancialPage'));
const CalfTreatmentHistoryPage = lazy(() => import('./pages/CalfTreatmentHistoryPage'));
const CowTreatmentHistoryPage = lazy(() => import('./pages/CowTreatmentHistoryPage'));
const CowSurgeriesPage = lazy(() => import('./pages/CowSurgeriesPage'));
const CowSurgeryMasterDataPage = lazy(() => import('./pages/CowSurgeryMasterDataPage'));
const Phase4RevenueEntriesPage = lazy(() => import('./pages/Phase4RevenueEntriesPage'));
const Phase4MilkSalesPage = lazy(() => import('./pages/Phase4MilkSalesPage'));
const Phase4LivestockSalesPage = lazy(() => import('./pages/Phase4LivestockSalesPage'));
const LivestockExitEventsPage = lazy(() => import('./pages/LivestockExitEventsPage'));
const LivestockMortalityNecropsyPage = lazy(() => import('./pages/LivestockMortalityNecropsyPage'));
const Phase4ProfitabilityAnalysisPage = lazy(() => import('./pages/Phase4ProfitabilityAnalysisPage'));
const FinancialEnginePage = lazy(() => import('./pages/FinancialEnginePage'));
const CullingReplacementPage = lazy(() => import('./pages/CullingReplacementPage'));
const HerdCapacityFlowPage = lazy(() => import('./pages/HerdCapacityFlowPage'));
const CorrectiveAlertCenterPage = lazy(() => import('./pages/CorrectiveAlertCenterPage'));
const ManagementScenarioPage = lazy(() => import('./pages/ManagementScenarioPage'));
const PredictiveModelCenterPage = lazy(() => import('./pages/PredictiveModelCenterPage'));
const SMSNotificationSettingsPage = lazy(() => import('./pages/SMSNotificationSettingsPage'));
const AdvancedAnalyticsCenterPage = lazy(() => import('./pages/AdvancedAnalyticsCenterPage'));
const ReportAnalyticsCenterPage = lazy(() => import('./pages/ReportAnalyticsCenterPage'));
const DecisionAnalyticsHubPage = lazy(() => import('./pages/DecisionAnalyticsHubPage'));
const DynamicReportBuilderPage = lazy(() => import('./pages/DynamicReportBuilderPage'));
const LegacyAdvancedReportsPage = lazy(() => import('./pages/LegacyAdvancedReportsPage'));
const LegacyReportCenterPage = lazy(() => import('./pages/LegacyReportCenterPage'));
























































































































































































// Protected Route Component
function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[45vh] items-center justify-center px-4" role="status" aria-live="polite">
      <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 text-center shadow-sm">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-primary-600" aria-hidden="true" />
        <p className="mt-3 text-sm font-bold text-slate-700">در حال آماده‌سازی صفحه...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, requiredPermission = null }) {
  const { isAuthenticated, user, authChecked, isBootstrapping } = useAuthStore();
  const location = useLocation();

  // Extra safety: prevent auth redirect loops when persisted state is out of sync with tokens
  const hasAnyToken = !!localStorage.getItem('access_token') || !!localStorage.getItem('refresh_token');

  if (!authChecked || isBootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-600">
        در حال بررسی ورود به سیستم...
      </div>
    );
  }

  if (!isAuthenticated || !hasAnyToken) {
    return <Navigate to="/login" replace />;
  }

  const resolvedPermission = requiredPermission || getPermissionForPath(location.pathname);
  if (resolvedPermission && !canUserAccess(user, resolvedPermission) && user?.role !== 'admin') {
    return <Layout><div className="card text-center py-12 text-gray-600">شما به این بخش دسترسی ندارید.</div></Layout>;
  }

  return <Layout>{children}</Layout>;
}

// Public Route Component (redirect if authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated, authChecked, isBootstrapping } = useAuthStore();

  const hasAnyToken = !!localStorage.getItem('access_token') || !!localStorage.getItem('refresh_token');

  if (!authChecked || isBootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-600">
        در حال بررسی ورود به سیستم...
      </div>
    );
  }

  if (isAuthenticated && hasAnyToken) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function LegacySectionRedirect({ fromPrefix, toPrefix }) {
  const location = useLocation();
  const targetPath = `${location.pathname.replace(fromPrefix, toPrefix)}${location.search || ''}${location.hash || ''}`;
  return <Navigate to={targetPath} replace />;
}

function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-300">404</h1>
        <p className="text-xl text-gray-500 mt-4">صفحه مورد نظر یافت نشد</p>
      </div>
    </div>
  );
}

function App() {
  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const { isAuthenticated, authChecked, isBootstrapping } = useAuthStore();
  const [banner, setBanner] = useState(null);
  const [systemUpdate, setSystemUpdate] = useState(null);
  const [excelImportUpload, setExcelImportUpload] = useState(null);
  const excelImportClearTimerRef = useRef(null);
  const systemUpdateActiveRef = useRef(false);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);


  useEffect(() => {
    const hasAnyToken = !!localStorage.getItem('access_token') || !!localStorage.getItem('refresh_token');
    if (!authChecked || isBootstrapping || !isAuthenticated || !hasAnyToken) {
      setSystemUpdate(null);
      systemUpdateActiveRef.current = false;
      return undefined;
    }

    let cancelled = false;

    const syncStatus = async () => {
      try {
        const response = await coreAPI.getSystemUpdateStatus();
        if (cancelled) return;
        setSystemUpdate(response?.data || null);
      } catch (error) {
        if (!cancelled) {
          setSystemUpdate((current) => current || null);
        }
      }
    };

    const handleSystemUpdateEvent = (event) => {
      if (cancelled) return;
      setSystemUpdate(event?.detail || null);
    };

    syncStatus();
    const timer = window.setInterval(syncStatus, 2000);
    window.addEventListener('damyar:system-update-status', handleSystemUpdateEvent);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('damyar:system-update-status', handleSystemUpdateEvent);
    };
  }, [authChecked, isAuthenticated, isBootstrapping]);

  useEffect(() => {
    const isActive = Boolean(systemUpdate?.active);
    const progressPercent = Number(systemUpdate?.progress_percent || 0);
    const baseMaintenanceMessage = systemUpdate?.message || 'دیتابیس در حال بروزرسانی است. لطفاً صبر کنید.';
    const maintenanceMessage = isActive && progressPercent > 0
      ? `${baseMaintenanceMessage} (${progressPercent}%)`
      : baseMaintenanceMessage;

    if (isActive) {
      setBanner({ type: 'warning', message: maintenanceMessage });
      if (!systemUpdateActiveRef.current) {
        systemUpdateActiveRef.current = true;
      }
      return;
    }

    if (systemUpdateActiveRef.current) {
      systemUpdateActiveRef.current = false;
      setBanner((current) => {
        if (current?.type === 'warning' && current?.message === maintenanceMessage) {
          return null;
        }
        return current;
      });
    }
  }, [systemUpdate]);

  useEffect(() => {
    const showBanner = (nextBanner) => {
      setBanner(nextBanner);
    };

    const handleOffline = () => {
      const payload = { type: 'warning', message: 'اتصال اینترنت یا شبکه قطع شده است. برخی عملیات تا بازگشت ارتباط انجام نمی‌شوند.' };
      showBanner(payload);
      toast.error(payload.message);
    };

    const handleOnline = () => {
      const payload = { type: 'success', message: 'ارتباط دوباره برقرار شد.' };
      showBanner(payload);
      toast.success(payload.message);
      window.setTimeout(() => {
        setBanner((current) => (current?.message === payload.message ? null : current));
      }, 4000);
    };

    const handleAuthExpired = () => {
      const payload = { type: 'error', message: 'نشست شما منقضی شد. برای ادامه دوباره وارد سامانه شوید.' };
      showBanner(payload);
      toast.error(payload.message);
    };

    const handleRequestError = (event) => {
      const payload = { type: 'error', message: event?.detail?.message || 'خطای ارتباط با سرور رخ داد.' };
      showBanner(payload);
      toast.error(payload.message);
    };

    const handleWriteResponseWarning = (event) => {
      const requestId = event?.detail?.requestId;
      const baseMessage = event?.detail?.message || 'اطلاعات ذخیره شد، اما پاسخ کامل سرور قابل نمایش نبود.';
      const message = requestId && !baseMessage.includes(requestId)
        ? `${baseMessage} (شناسه پیگیری: ${requestId})`
        : baseMessage;
      showBanner({ type: 'warning', message });
      toast.warning(message);
    };

    const handleRequestRetry = (event) => {
      const detail = event?.detail || {};
      const method = String(detail.method || 'GET').toUpperCase();
      // Background reads retry silently. For a write, explain that the same
      // idempotency key is being reused so the operator does not click again.
      if (['GET', 'HEAD', 'OPTIONS'].includes(method)) return;
      const attempt = Number(detail.attempt || 1);
      const message = `پاسخ سرور کند یا قطع شد؛ تلاش امن شماره ${attempt.toLocaleString('fa-IR')} با همان شناسه درخواست در حال انجام است. دکمه ثبت را دوباره نزنید.`;
      showBanner({ type: 'warning', message });
      toast.loading(message, { id: 'damyar-write-retry', duration: 5000 });
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener('damyar:auth-expired', handleAuthExpired);
    window.addEventListener('damyar:request-error', handleRequestError);
    window.addEventListener('damyar:write-response-warning', handleWriteResponseWarning);
    window.addEventListener('damyar:request-retry', handleRequestRetry);

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      handleOffline();
    }

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('damyar:auth-expired', handleAuthExpired);
      window.removeEventListener('damyar:request-error', handleRequestError);
      window.removeEventListener('damyar:write-response-warning', handleWriteResponseWarning);
      window.removeEventListener('damyar:request-retry', handleRequestRetry);
    };
  }, []);



  useEffect(() => {
    const handleExcelImportProgress = (event) => {
      const detail = event?.detail || null;
      if (!detail) return;
      if (excelImportClearTimerRef.current) {
        window.clearTimeout(excelImportClearTimerRef.current);
        excelImportClearTimerRef.current = null;
      }
      setExcelImportUpload((current) => ({ ...(current || {}), ...detail }));
      if (detail.status === 'succeeded' || detail.status === 'failed') {
        excelImportClearTimerRef.current = window.setTimeout(() => {
          setExcelImportUpload((current) => (current?.id === detail.id ? null : current));
          excelImportClearTimerRef.current = null;
        }, 6000);
      }
    };

    window.addEventListener('damyar:excel-import-progress', handleExcelImportProgress);
    return () => {
      window.removeEventListener('damyar:excel-import-progress', handleExcelImportProgress);
      if (excelImportClearTimerRef.current) {
        window.clearTimeout(excelImportClearTimerRef.current);
        excelImportClearTimerRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <PersianDateGuard />
      <AppShellBanner banner={banner} onClose={() => setBanner(null)} />
      {systemUpdate?.active ? (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-amber-200 p-6 mx-4 text-right">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">دیتابیس در حال بروزرسانی است</h3>
                <p className="text-sm text-slate-600 mt-1">{systemUpdate?.scope_label || 'ایمپورت اکسل دام‌ها'}</p>
              </div>
              {Number(systemUpdate?.total_steps || 0) > 0 ? (
                <div className="text-2xl font-black text-amber-600">{Number(systemUpdate?.progress_percent || 0)}%</div>
              ) : Number(systemUpdate?.processed_steps || 0) > 0 ? (
                <div className="text-sm font-black text-amber-700 rounded-full bg-amber-50 border border-amber-200 px-3 py-2">
                  {Number(systemUpdate?.processed_steps || 0).toLocaleString('fa-IR')} ردیف پردازش شد
                </div>
              ) : (
                <div className="text-sm font-black text-amber-700 rounded-full bg-amber-50 border border-amber-200 px-3 py-2">در حال آماده‌سازی…</div>
              )}
            </div>
            <p className="text-sm text-slate-700 leading-7 mb-4">{systemUpdate?.message || 'لطفاً صبر کنید تا فرآیند واردسازی کامل شود. در این مدت ثبت، ویرایش و حذف اطلاعات موقتاً غیرفعال است.'}</p>
            <div className="h-3 w-full rounded-full bg-slate-200 overflow-hidden mb-3">
              {Number(systemUpdate?.total_steps || 0) > 0 ? (
                <div className="h-full rounded-full bg-amber-500 transition-all duration-500" style={{ width: `${Math.max(1, Math.min(Number(systemUpdate?.progress_percent || 0), 100))}%` }} />
              ) : (
                <div className="h-full w-1/3 rounded-full bg-amber-500 animate-pulse" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-slate-600">
              <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
                <div className="text-slate-500">ردیف‌های پردازش‌شده</div>
                <div className="font-bold text-slate-900 mt-1">{Number(systemUpdate?.processed_steps || 0).toLocaleString('fa-IR')}</div>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
                <div className="text-slate-500">کل ردیف‌ها</div>
                <div className="font-bold text-slate-900 mt-1">{Number(systemUpdate?.total_steps || 0).toLocaleString('fa-IR')}</div>
              </div>
            </div>
            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 px-3 py-3 text-xs text-amber-800 leading-6">
              در این بازه، برای جلوگیری از تداخل داده، ثبت و ویرایش اطلاعات برای همه کاربران موقتاً بسته شده است.
            </div>
          </div>
        </div>
      ) : null}
      {!systemUpdate?.active && excelImportUpload ? (
        <div className="fixed bottom-4 left-4 z-[1050] w-[min(420px,calc(100vw-2rem))] rounded-2xl border border-slate-200 bg-white shadow-2xl p-4 text-right no-print">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-bold text-slate-900">پیشرفت ایمپورت اکسل</div>
              <div className="mt-1 text-xs text-slate-500 truncate max-w-[300px]">{excelImportUpload.file_name || excelImportUpload.url || 'فایل اکسل'}</div>
            </div>
            <div className={`text-lg font-black ${excelImportUpload.status === 'failed' ? 'text-red-600' : excelImportUpload.status === 'succeeded' ? 'text-emerald-600' : 'text-amber-600'}`}>
              {Number(excelImportUpload.progress_percent || 0)}%
            </div>
          </div>
          <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className={`h-full rounded-full transition-all duration-300 ${excelImportUpload.status === 'failed' ? 'bg-red-500' : excelImportUpload.status === 'succeeded' ? 'bg-emerald-500' : 'bg-amber-500'}`}
              style={{ width: `${Math.max(2, Math.min(100, Number(excelImportUpload.progress_percent || 0)))}%` }}
            />
          </div>
          <div className="mt-2 text-xs leading-6 text-slate-600">
            {excelImportUpload.message || 'در حال پردازش فایل اکسل...'}
            {excelImportUpload.import_batch ? <span className="block text-slate-400">بچ: {excelImportUpload.import_batch}</span> : null}
          </div>
          <button
            type="button"
            className="mt-2 text-xs font-bold text-slate-500 hover:text-slate-800"
            onClick={() => setExcelImportUpload(null)}
          >
            بستن
          </button>
        </div>
      ) : null}
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/daily-plan"
        element={
          <ProtectedRoute>
            <DailyPlanPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/ops"
        element={
          <ProtectedRoute>
            <MorningDashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro"
        element={
          <ProtectedRoute>
            <ReproDashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/planner"
        element={
          <ProtectedRoute>
            <ReproPlannerPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/health"
        element={
          <ProtectedRoute>
            <HealthDashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/health/workbench"
        element={
          <ProtectedRoute>
            <HealthTreatmentWorkbenchPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/health/hospital"
        element={
          <ProtectedRoute>
            <HospitalAdmissionsPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/fresh-cow-monitoring"
        element={
          <ProtectedRoute>
            <FreshCowMonitoringPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/lab-tests"
        element={
          <ProtectedRoute>
            <LabTestsPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/vet-command-center"
        element={
          <ProtectedRoute>
            <VetCommandCenterPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/vet-smart-alerts"
        element={
          <ProtectedRoute>
            <VetSmartAlertsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/lists"
        element={
          <ProtectedRoute>
            <Navigate to="/lists/saved?tab=templates" replace />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportCommandCenterPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/command-center"
        element={
          <ProtectedRoute>
            <ReportCommandCenterPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/reports/calf"
        element={
          <ProtectedRoute>
            <CalfReportCenterPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/data-quality"
        element={
          <ProtectedRoute requiredPermission="data_quality.view">
            <DataQualityCenterPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/kpi-center"
        element={
          <ProtectedRoute>
            <KpiCenterPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/herd-dashboard"
        element={
          <ProtectedRoute>
            <HerdExecutiveDashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/operational"
        element={
          <ProtectedRoute>
            <Phase1OperationalReportsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/operational/phase2"
        element={
          <ProtectedRoute>
            <Phase2OperationalFertilityReportsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/analytical"
        element={
          <ProtectedRoute>
            <Phase1AnalyticalReportsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/analytical/fertility"
        element={
          <ProtectedRoute>
            <Phase4FertilityAnalyticsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/analytical/production-health-calf"
        element={
          <ProtectedRoute>
            <Phase5ProductionHealthCalfAnalyticsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/analytical/baharband-people-quality"
        element={
          <ProtectedRoute>
            <Phase6BaharbandPeopleQualityAnalyticsPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/reports/analytical/lactation-heifer"
        element={
          <ProtectedRoute>
            <Phase24LactationHeiferAnalyticsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/analytical/heifer-service-age-gestation"
        element={
          <ProtectedRoute>
            <HeiferServiceAgeGestationReportPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/analytical/calving-transition"
        element={
          <ProtectedRoute>
            <Phase22CalvingTransitionAnalyticsPage />
          </ProtectedRoute>
        }
      />



      <Route
        path="/reports/management-executive"
        element={
          <ProtectedRoute>
            <Phase7ManagementExecutiveReportPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/smart-alert-center"
        element={
          <ProtectedRoute>
            <Phase9SmartAlertCenterPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/management-settings"
        element={
          <ProtectedRoute>
            <Phase10ManagementSettingsPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/reports/management-cockpit"
        element={
          <ProtectedRoute>
            <ManagementCockpitPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/reports/analytics-lab"
        element={
          <ProtectedRoute requiredPermission="analytics_lab.view">
            <AnalystLabPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/pregnancy-cycles"
        element={
          <ProtectedRoute requiredPermission="pregnancy_cycles.view">
            <PregnancyCycleAnalyticsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/custom-builder"
        element={
          <ProtectedRoute>
            <Phase11CustomReportBuilderPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/performance-center"
        element={
          <ProtectedRoute>
            <Phase12ReportPerformanceCenterPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/herd-snapshot-history"
        element={
          <ProtectedRoute>
            <Phase13HerdSnapshotHistoryPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/management-economic-decision"
        element={
          <ProtectedRoute>
            <Phase14EconomicDecisionAnalyticsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/predictive-risk"
        element={
          <ProtectedRoute>
            <Phase15PredictiveRiskAnalyticsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/enterprise-analytics"
        element={
          <ProtectedRoute>
            <EnterpriseAnalyticsPage />
          </ProtectedRoute>
        }
      />



      <Route
        path="/reports/legacy-center"
        element={
          <ProtectedRoute>
            <LegacyReportCenterPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/legacy-advanced"
        element={
          <ProtectedRoute>
            <LegacyAdvancedReportsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/dynamic-builder"
        element={
          <ProtectedRoute>
            <DynamicReportBuilderPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/analytics-center"
        element={
          <ProtectedRoute>
            <ReportAnalyticsCenterPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/decision-analytics"
        element={
          <ProtectedRoute>
            <DecisionAnalyticsHubPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/advanced-analytics"
        element={
          <ProtectedRoute>
            <AdvancedAnalyticsCenterPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/sms-notifications"
        element={
          <ProtectedRoute>
            <SMSNotificationSettingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/site-comparison"
        element={
          <ProtectedRoute>
            <SiteComparisonPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/economic"
        element={
          <ProtectedRoute>
            <EconomicWorkbenchPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/economic/workbench"
        element={
          <ProtectedRoute>
            <EconomicWorkbenchPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/hormone-therapy"
        element={
          <ProtectedRoute>
            <TreatmentsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/hormone-therapy/new"
        element={
          <ProtectedRoute>
            <NewTreatmentPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/hormone-therapy/:id"
        element={
          <ProtectedRoute>
            <TreatmentBatchDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/hormone-therapy/:id/edit"
        element={
          <ProtectedRoute>
            <NewTreatmentPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/hormone-therapy-protocols"
        element={
          <ProtectedRoute>
            <ProtocolsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/hormone-therapy-protocols/new"
        element={
          <ProtectedRoute>
            <ProtocolFormPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/hormone-therapy-protocols/:id/edit"
        element={
          <ProtectedRoute>
            <ProtocolFormPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/treatments/*"
        element={
          <ProtectedRoute>
            <LegacySectionRedirect fromPrefix="/treatments" toPrefix="/repro/hormone-therapy" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/protocols/*"
        element={
          <ProtectedRoute>
            <LegacySectionRedirect fromPrefix="/protocols" toPrefix="/repro/hormone-therapy-protocols" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/cows"
        element={
          <ProtectedRoute>
            <CowsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/cows/duplicates"
        element={
          <ProtectedRoute>
            <DuplicateCowMergePage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/cows/daily-herd-snapshots"
        element={
          <ProtectedRoute requiredPermission="cows.view">
            <DailyHerdSnapshotPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/cows/movement-history"
        element={
          <ProtectedRoute>
            <CowMovementHistoryReportPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/cows/individual"
        element={
          <ProtectedRoute>
            <CowIndividualSelectPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/cows/:id/individual"
        element={
          <ProtectedRoute>
            <CowCardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/cows/:id/individual/legacy"
        element={
          <ProtectedRoute>
            <CowIndividualPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/lists/saved"
        element={
          <ProtectedRoute>
            <SavedListsPage />
          </ProtectedRoute>
        }
      />



      <Route
        path="/operational-people"
        element={
          <ProtectedRoute>
            <OperationalPeoplePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/registered-data-edit"
        element={
          <ProtectedRoute requiredPermission="registered_data_edit.view">
            <RegisteredDataEditPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/baharbands"
        element={
          <ProtectedRoute>
            <BaharbandsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/calves"
        element={
          <ProtectedRoute>
            <CalvesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/calf-photo-sketches"
        element={
          <ProtectedRoute>
            <CalfPhotoSketchPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/calf-image-search-history"
        element={
          <ProtectedRoute>
            <CalfImageSearchHistoryPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/calf-events"
        element={
          <ProtectedRoute>
            <CalfEventsPage />
          </ProtectedRoute>
        }
      />


<Route
  path="/calf-daily-tasks"
  element={
    <ProtectedRoute>
      <CalfDailyTasksPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/calf-growth-weighings"
  element={
    <ProtectedRoute>
      <CalfGrowthWeighingPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/calf-treatment-sheets"
  element={
    <ProtectedRoute>
      <CalfTreatmentSheetsPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/cow-treatment-sheets"
  element={
    <ProtectedRoute>
      <CowTreatmentSheetsPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/calf-treatment-protocols"
  element={
    <ProtectedRoute>
      <CalfTreatmentProtocolsPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/cow-treatment-protocols"
  element={
    <ProtectedRoute>
      <CowTreatmentProtocolsPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/calf-treatment-ops"
  element={
    <ProtectedRoute>
      <CalfTreatmentOpsBoardPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/calf-treatment-financial"
  element={
    <ProtectedRoute>
      <CalfTreatmentFinancialPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/calf-treatment-history"
  element={
    <ProtectedRoute>
      <CalfTreatmentHistoryPage />
    </ProtectedRoute>
  }
/>



<Route
  path="/cow-surgeries"
  element={
    <ProtectedRoute>
      <CowSurgeriesPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/cow-surgery-master-data"
  element={
    <ProtectedRoute>
      <CowSurgeryMasterDataPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/cow-treatment-history"
  element={
    <ProtectedRoute>
      <CowTreatmentHistoryPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/calf-analytics"
  element={
    <ProtectedRoute>
      <CalfAnalyticsDashboardPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/calf-growth-analytics"
  element={
    <ProtectedRoute>
      <CalfGrowthAnalyticsPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/calf-clinical-scoring"
  element={
    <ProtectedRoute>
      <CalfClinicalScoringPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/calf-mortality-report"
  element={
    <ProtectedRoute>
      <CalfMortalityReportPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/livestock-exit-causes"
  element={
    <ProtectedRoute>
      <LivestockExitCausesPage />
    </ProtectedRoute>
  }
/>

      <Route
        path="/livestock-exit-events"
        element={
          <ProtectedRoute>
            <LivestockExitEventsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/livestock-mortality-necropsy"
        element={
          <ProtectedRoute requiredPermission="livestock_exit_events.view">
            <LivestockMortalityNecropsyPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/livestock-necropsy"
        element={
          <ProtectedRoute requiredPermission="livestock_exit_events.view">
            <LivestockMortalityNecropsyPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/calf-milk-records"
        element={
          <ProtectedRoute>
            <CalfMilkRecordsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/cow-documents"
        element={
          <ProtectedRoute>
            <CowDocumentsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/drugs"
        element={
          <ProtectedRoute>
            <DrugsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/drug-lots"
        element={
          <ProtectedRoute>
            <DrugLotsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/medication-withdrawal-report"
        element={
          <ProtectedRoute>
            <MedicationWithdrawalReportPage />
          </ProtectedRoute>
        }
      />



      <Route
        path="/treatment-outcome-analytics"
        element={
          <ProtectedRoute>
            <TreatmentOutcomeAnalyticsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/medication-batches"
        element={
          <ProtectedRoute>
            <MedicationBatchesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/disease-master-data"
        element={
          <ProtectedRoute>
            <DiseaseMasterDataPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/vet-health-report"
        element={
          <ProtectedRoute>
            <VetHealthReportPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/udder-lameness-report"
        element={
          <ProtectedRoute>
            <UdderLamenessReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/repro/udder-lameness-operations"
        element={
          <ProtectedRoute>
            <UdderLamenessOperationsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/vet-phase2-summary"
        element={
          <ProtectedRoute>
            <VetPhase2SummaryPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-inventory"
        element={
          <ProtectedRoute requiredPermission="feed_inventory.view">
            <FeedInventoryPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-items"
        element={
          <ProtectedRoute requiredPermission="feed_master_data.view">
            <FeedInventoryPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-categories"
        element={
          <ProtectedRoute requiredPermission="feed_master_data.view">
            <FeedCategoriesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-waste-causes"
        element={
          <ProtectedRoute requiredPermission="feed_master_data.view">
            <FeedWasteCausesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-loss-causes"
        element={
          <ProtectedRoute requiredPermission="feed_master_data.view">
            <FeedLossCausesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-residual-causes"
        element={
          <ProtectedRoute requiredPermission="feed_master_data.view">
            <FeedResidualCausesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-overhead-types"
        element={
          <ProtectedRoute requiredPermission="feed_master_data.view">
            <FeedOverheadTypesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feeding-groups"
        element={
          <ProtectedRoute requiredPermission="feed_master_data.view">
            <FeedingGroupsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-rations"
        element={
          <ProtectedRoute requiredPermission="feed_operations.view">
            <RationsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-rations/:id"
        element={
          <ProtectedRoute requiredPermission="feed_operations.view">
            <RationDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-purchases"
        element={
          <ProtectedRoute requiredPermission="procurement.view">
            <FeedPurchasesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-planned-consumptions"
        element={
          <ProtectedRoute requiredPermission="feed_operations.view">
            <FeedPlannedConsumptionsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-scale-tickets"
        element={
          <ProtectedRoute requiredPermission="feed_operations.view">
            <FeedScaleTicketsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-scale-distributions"
        element={
          <ProtectedRoute requiredPermission="feed_operations.view">
            <FeedScaleDistributionsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-consumptions"
        element={
          <ProtectedRoute requiredPermission="feed_operations.view">
            <FeedConsumptionPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-wastes"
        element={
          <ProtectedRoute requiredPermission="feed_operations.view">
            <FeedWastesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-losses"
        element={
          <ProtectedRoute requiredPermission="feed_operations.view">
            <FeedLossesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-residuals"
        element={
          <ProtectedRoute requiredPermission="feed_operations.view">
            <FeedResidualsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-overheads"
        element={
          <ProtectedRoute requiredPermission="feed_operations.view">
            <FeedOverheadsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-operational-cost"
        element={
          <ProtectedRoute requiredPermission="feed_operations.view">
            <FeedOperationalCostPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-dashboard"
        element={
          <ProtectedRoute requiredPermission="inventory_reports.view">
            <FeedDashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-start-guide"
        element={
          <ProtectedRoute requiredPermission={['feed_master_data.view', 'feed_inventory.view', 'feed_operations.view', 'inventory_reports.view']}>
            <FeedStartGuidePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-excel-import"
        element={
          <ProtectedRoute requiredPermission={['feed_master_data.view', 'feed_inventory.view', 'feed_operations.view', 'procurement.view']}>
            <FeedExcelImportPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-iofc"
        element={
          <ProtectedRoute requiredPermission="inventory_reports.view">
            <FeedIOFCPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-nutrition-inventory"
        element={
          <ProtectedRoute requiredPermission={['feed_master_data.view', 'feed_inventory.view', 'feed_operations.view', 'inventory_reports.view']}>
            <FeedInventoryRationFormulaPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-baharband-rations"
        element={
          <ProtectedRoute requiredPermission={['feed_operations.view', 'baharbands.view']}>
            <FeedBaharbandRationPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-daily-fast-entry"
        element={
          <ProtectedRoute requiredPermission="feed_operations.view">
            <FeedDailyFastEntryPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-management-reports"
        element={
          <ProtectedRoute requiredPermission="inventory_reports.view">
            <FeedManagementReportsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-performance-analytics"
        element={
          <ProtectedRoute requiredPermission="inventory_reports.view">
            <FeedPerformanceAnalyticsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed-alert-dashboard"
        element={
          <ProtectedRoute requiredPermission="inventory_reports.view">
            <FeedAlertDashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/calf-feed-age-intake"
        element={
          <ProtectedRoute requiredPermission={['calf_reports.view', 'feed_operations.view']}>
            <CalfFeedAgeIntakePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/milk-baharband-performance"
        element={
          <ProtectedRoute>
            <MilkBaharbandPerformancePage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/suppliers"
        element={
          <ProtectedRoute>
            <SuppliersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/counterparties"
        element={
          <ProtectedRoute requiredPermission={['counterparties.view', 'milk_sales.view', 'financial_data.view']}>
            <CounterpartiesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/supply-inventory"
        element={
          <ProtectedRoute>
            <SupplyInventoryPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/supply-purchases"
        element={
          <ProtectedRoute>
            <SupplyPurchasesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/supply-consumptions"
        element={
          <ProtectedRoute>
            <SupplyConsumptionPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/purchase-requests"
        element={
          <ProtectedRoute>
            <PurchaseRequestsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/purchase-orders"
        element={
          <ProtectedRoute>
            <PurchaseOrdersPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/storage-locations"
        element={
          <ProtectedRoute>
            <StorageLocationsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/semen-storage-positions"
        element={
          <ProtectedRoute>
            <SemenStoragePositionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/goods-receipts"
        element={
          <ProtectedRoute>
            <GoodsReceiptsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/opening-inventories"
        element={
          <ProtectedRoute>
            <OpeningInventoriesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/stock-counts"
        element={
          <ProtectedRoute>
            <StockCountsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/inventory-on-hand"
        element={
          <ProtectedRoute>
            <InventoryOnHandPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/inventory-alerts"
        element={
          <ProtectedRoute>
            <InventoryAlertsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/phase3/operational-summary"
        element={
          <ProtectedRoute>
            <Phase3OperationalSummaryPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/phase4/executive-dashboard"
        element={
          <ProtectedRoute requiredPermission="can_view_management_reports">
            <Phase4ExecutiveDashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/phase4/cost-centers"
        element={
          <ProtectedRoute requiredPermission="can_manage_financial_data">
            <Phase4CostCentersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/phase4/operating-expenses"
        element={
          <ProtectedRoute requiredPermission="can_manage_financial_data">
            <Phase4OperatingExpensesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/phase4/revenue-entries"
        element={
          <ProtectedRoute requiredPermission="can_manage_financial_data">
            <Phase4RevenueEntriesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/phase4/milk-sales"
        element={
          <ProtectedRoute requiredPermission="milk_sales.view">
            <Phase4MilkSalesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/phase4/livestock-sales"
        element={
          <ProtectedRoute requiredPermission="can_manage_financial_data">
            <Phase4LivestockSalesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/phase4/management-cost-analysis"
        element={
          <ProtectedRoute requiredPermission="can_view_management_reports">
            <Phase4ManagementCostAnalysisPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/phase4/profitability-analysis"
        element={
          <ProtectedRoute requiredPermission="can_view_management_reports">
            <Phase4ProfitabilityAnalysisPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/finance/engine"
        element={
          <ProtectedRoute requiredPermission="can_view_management_reports">
            <FinancialEnginePage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/finance/culling-replacement"
        element={
          <ProtectedRoute requiredPermission="can_view_management_reports">
            <CullingReplacementPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/herd/capacity-flow"
        element={
          <ProtectedRoute requiredPermission="can_view_management_reports">
            <HerdCapacityFlowPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/alerts/corrective-center"
        element={
          <ProtectedRoute requiredPermission="can_view_management_reports">
            <CorrectiveAlertCenterPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/management/scenarios"
        element={
          <ProtectedRoute requiredPermission="can_view_management_reports">
            <ManagementScenarioPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/management/predictive-models"
        element={
          <ProtectedRoute requiredPermission="can_view_management_reports">
            <PredictiveModelCenterPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/phase4/allocation-rules"
        element={
          <ProtectedRoute requiredPermission="can_manage_financial_data">
            <Phase4AllocationRulesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/backups"
        element={
          <ProtectedRoute requiredPermission="system_backups.view">
            <BackupManagerPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/power-user"
        element={
          <ProtectedRoute>
            <PowerUserPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/estrus/new"
        element={
          <ProtectedRoute>
            <EstrusNewPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/estrus"
        element={
          <ProtectedRoute>
            <EstrusEventsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/vet-visit"
        element={
          <ProtectedRoute>
            <VetVisitPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/repro/visit-plans"
        element={
          <ProtectedRoute>
            <ReproVisitPlansPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/milk-records"
        element={
          <ProtectedRoute>
            <MilkRecordsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/milk-quality-report"
        element={
          <ProtectedRoute>
            <MilkQualityReportPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/milk-advanced-analytics"
        element={
          <ProtectedRoute>
            <AdvancedMilkAnalyticsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/milk-health-impact"
        element={
          <ProtectedRoute>
            <MilkHealthImpactPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/fertility-health-impact"
        element={
          <ProtectedRoute>
            <FertilityHealthImpactPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/services"
        element={
          <ProtectedRoute>
            <ReproServicesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/semen-inventory"
        element={
          <ProtectedRoute>
            <SemenInventoryPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/pregnancy-checks"
        element={
          <ProtectedRoute>
            <ReproPregnancyChecksPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/revisits"
        element={
          <ProtectedRoute>
            <ReproRevisitsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/abortions"
        element={
          <ProtectedRoute>
            <ReproAbortionsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/dryoffs"
        element={
          <ProtectedRoute>
            <ReproDryOffsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/calvings"
        element={
          <ProtectedRoute>
            <ReproCalvingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/preventive-care"
        element={
          <ProtectedRoute>
            <PreventiveCarePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/udder-health"
        element={
          <ProtectedRoute>
            <UdderHealthPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/lameness-events"
        element={
          <ProtectedRoute>
            <LamenessEventsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/timeline"
        element={
          <ProtectedRoute>
            <ReproTimelinePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/reports"
        element={
          <ProtectedRoute>
            <ReproComprehensiveReportsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/reports/legacy-fertility"
        element={
          <ProtectedRoute>
            <ReproLegacyFertilityReportsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/service-days-range"
        element={
          <ProtectedRoute>
            <ReproServiceDaysRangePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/calving-forecast"
        element={
          <ProtectedRoute>
            <ReproCalvingForecastPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/fertility-neglected-milking"
        element={
          <ProtectedRoute>
            <ReproFertilityNeglectedMilkingPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repro/calving-information"
        element={
          <ProtectedRoute>
            <ReproCalvingInformationReportPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/alerts"
        element={
          <ProtectedRoute>
            <AlertsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cows/:id/lactation-curve"
        element={
          <ProtectedRoute>
            <LactationCurvePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vaccination-protocols"
        element={
          <ProtectedRoute>
            <VaccinationProtocolsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/calf-milk-protocols"
        element={
          <ProtectedRoute>
            <CalfMilkProtocolsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/calf-milk-monthly-report"
        element={
          <ProtectedRoute>
            <CalfMilkMonthlyReportPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute>
            <AuditLogsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/user-daily-activity"
        element={
          <ProtectedRoute requiredPermission="audit_logs.view">
            <UserDailyActivityPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/import-jobs"
        element={
          <ProtectedRoute requiredPermission={['audit_logs.view', 'cows.view', 'baharbands.view', 'milk_records.view', 'cow_documents.view', 'calf_events.view', 'preventive_care.view', 'lameness_events.view', 'services.view', 'pregnancy_checks.view', 'dryoffs.view', 'calvings.view', 'semen_inventory.view', 'treatments.view', 'inventory_documents.view', 'feed_master_data.view', 'feed_inventory.view', 'feed_operations.view', 'procurement.view', 'financial_data.view']}>
            <ImportJobsPage />
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
